import {
  CheckboxProps,
  FormControlLabel,
  FormControlLabelProps,
  Checkbox as MaterialCheckbox,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useMemo } from "react";
import { disabledGray, disabledGrayBackground, lightBackground } from "shared/designSystem/theme";

export type Props = CheckboxProps &
  Omit<FormControlLabelProps, "control" | "className"> & {
    filled?: boolean;
    preventDefaultClick?: boolean;
  };

const Checkbox = React.forwardRef(
  ({ label, value, disabled, filled, onChange, preventDefaultClick = false, ...props }: Props, ref) => {
    const labelElement = useMemo(() => <Typography fontSize={12}>{label}</Typography>, [label]);

    return (
      <StyledLabel
        {...props}
        label={labelElement}
        ref={ref}
        onChange={onChange}
        onClick={e => {
          if (preventDefaultClick) e.preventDefault();
        }}
        control={
          <BlackCheckbox
            checked={!!value}
            disabled={disabled}
            size="small"
            sx={
              filled
                ? {
                    "&.Mui-checked": {
                      // Checkmark contrasts with the checked pill background
                      // (text.primary), which inverts per light/dark mode.
                      color: theme => theme.palette.background.paper,
                    },
                  }
                : {}
            }
          />
        }
      />
    );
  },
);

const StyledLabel = styled(FormControlLabel)({
  marginLeft: 0,
  textWrap: "nowrap",
});

const BlackCheckbox = styled(MaterialCheckbox)(({ theme }) => ({
  "&.MuiCheckbox-root": {
    marginLeft: 0,
    paddingLeft: 0,
    paddingRight: "4px",
    color: theme.palette.text.primary,
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme, value, filled, disabled }) => {
  if (!filled) return;

  const isDark = theme.palette.mode === "dark";

  // Checked pill uses the primary text color as its fill (black in light, light
  // in dark) with the paper color as its content, so the "on" state reads as a
  // solid inverse chip in both modes. Light-mode values are unchanged.
  let backgroundColor = value
    ? theme.palette.text.primary
    : isDark
      ? theme.palette.background.default
      : lightBackground;

  let color = value ? theme.palette.background.paper : theme.palette.text.primary;
  let borderColor = theme.palette.text.primary;

  if (disabled) {
    backgroundColor = isDark ? theme.palette.action.disabledBackground : disabledGrayBackground;
    color = disabledGray;
    borderColor = disabledGray;
  }

  const baseStyle = {
    height: "30px",
    padding: theme.spacing(1),
    border: `1px solid ${borderColor}`,
    borderRadius: theme.spacing(0.5),
    color,
    backgroundColor,

    "& .MuiTypography-root": {
      fontSize: theme.typography.pxToRem(14),
    },
  };

  return baseStyle;
});

export default StyledCheckbox;
